import { useState, useRef, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from '_common/hooks';
import { PasswordInput, Input, InputField, Link, Button, Paragraph } from '@ets/design-system';
import { resetLoginErrors, signIn, clearFeedback } from 'Auth/redux/authSlice';
import { useSelector } from '_common/hooks';

export const TEST_IDS = {
  INPUT: {
    USERNAME: 'username',
    PASSWORD: 'password',
  },
  BUTTON: 'signIn',
};

const FreshLogin = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const usernameRef = useRef<HTMLInputElement>(null);
  const loading = useSelector((state) => state.auth.loading);
  const feedback = useSelector((state) => state.auth.feedback);
  useEffect(() => {
    usernameRef.current?.focus();

    return () => {
      dispatch(resetLoginErrors());
    };
  }, []);

  useEffect(() => {
    if (feedback?.type === 'error') {
      usernameRef.current?.focus();
    }
  }, [feedback?.type]);

  const handleSignin = () => {
    if (username.length > 0 && password.length > 0) {
      dispatch(clearFeedback());
      dispatch(signIn({ params: { username, password } }));
    }
  };
  return (
    <div>
      <InputField
        size="l"
        label={intl.formatMessage({ id: 'EMAIL_ADDRESS_LABEL' })}
        testId="email-username"
      >
        <span data-testid={TEST_IDS.INPUT.USERNAME}>
          <Input
            hasError={feedback?.type === 'error'}
            ref={usernameRef}
            size="l"
            placeholder={intl.formatMessage({ id: 'EMAIL_ADDRESS_PLACEHOLDER' })}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            testId={TEST_IDS.INPUT.USERNAME}
            tabIndex={1}
          />
        </span>
      </InputField>
      {/** This form wrap avoids browser verbose displaying password value
       * And it is needed to prevent the form from submitting on suffix click(eye icon)
       */}
      <form onSubmit={(e) => e.preventDefault()}>
        <InputField
          size="l"
          label={intl.formatMessage({ id: 'PASSWORD' })}
          suffix={
            <Link
              testId="forgot-password"
              tabIndex={3}
              size="s"
              variant="primary"
              href="/auth/recover"
            >
              <Paragraph size="s" weight="500" className="pb-2 pl-8">
                <FormattedMessage id="FORGOT_YOUR_PASSWORD" />
              </Paragraph>
            </Link>
          }
          className="mt-4"
          testId="password"
        >
          <span data-testid={TEST_IDS.INPUT.PASSWORD}>
            <PasswordInput
              hasError={feedback?.type === 'error'}
              size="l"
              placeholder={intl.formatMessage({ id: 'ENTER_PASSWORD' })}
              autoComplete="off"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSignin();
                }
              }}
              testId={TEST_IDS.INPUT.PASSWORD}
              tabIndex={1}
            />
          </span>
        </InputField>
      </form>
      <div style={{ marginTop: '1rem' }}>
        <Button
          variant="primary"
          size="l"
          style={{ margin: '1rem 0 0 0' }}
          fullWidth
          onClick={handleSignin}
          disabled={username.length === 0 || password.length === 0}
          testId={TEST_IDS.BUTTON}
          loading={loading}
          tabIndex={1}
        >
          <FormattedMessage id="SIGN_IN" />
        </Button>
      </div>
    </div>
  );
};

export default FreshLogin;
